/* global gdAlertify */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../config/axiosConfig";
import GdTable from "./GdTable";
import { formatDate } from "../../utils/utils";
import {
    getNoEnviarPacienteStatus,
    isNoEnviarRecordActive,
} from "../../utils/noEnviarPaciente";

const pendingHistoryLoads = new Map();

const NoEnviarPacienteAuxiliarModal = ({
    isOpen,
    onClose,
    onChange = () => {},
    auxiliarID,
    nombreAuxiliar,
    pacienteID,
    nombrePaciente,
}) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [observacion, setObservacion] = useState("");

    const loadHistory = useCallback(async () => {
        if (!auxiliarID || !pacienteID) return;

        setLoading(true);
        const requestKey = `${auxiliarID}-${pacienteID}`;
        try {
            let request = pendingHistoryLoads.get(requestKey);

            if (!request) {
                request = axiosInstance.get("/auxiliar/no-enviar-paciente/lista", {
                    params: {
                        auxiliarID,
                        pacienteID,
                    },
                });
                pendingHistoryLoads.set(requestKey, request);
            }

            const response = await request;
            setHistory(response.data?.data || []);
        } catch (error) {
            if (error?.response?.status === 404) {
                setHistory([]);
                return;
            }

            console.error("Error al cargar historial No Enviar:", error);
            gdAlertify.notiError("Error al cargar historial de No Enviar.");
        } finally {
            pendingHistoryLoads.delete(requestKey);
            setLoading(false);
        }
    }, [auxiliarID, pacienteID]);

    useEffect(() => {
        if (!isOpen) return;

        setObservacion("");
        if (auxiliarID && pacienteID) {
            loadHistory();
        }
    }, [isOpen, auxiliarID, pacienteID, loadHistory]);

    const activeRecord = useMemo(
        () => history.find((record) => isNoEnviarRecordActive(record)) || null,
        [history]
    );

    const handleSave = async (e) => {
        e.preventDefault();

        if (!observacion.trim()) {
            gdAlertify.notiError("Debe ingresar una observacion.");
            return;
        }

        if (activeRecord) {
            gdAlertify.notiError("Ya existe una marca activa. Debe eliminarla antes de crear otra.");
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await axiosInstance.post("/auxiliar/no-enviar-paciente/guardar", {
                auxiliar_id: auxiliarID,
                paciente_id: pacienteID,
                observacion: observacion.trim(),
            });

            if (response.data?.error) {
                gdAlertify.notiError(response.data.message || "No se pudo guardar la marca.");
                return;
            }

            gdAlertify.notiSuccess(response.data?.message || "Marca registrada correctamente.");
            setObservacion("");
            await loadHistory();
            onChange();
        } catch (error) {
            console.error("Error al guardar No Enviar:", error);
            gdAlertify.notiError("Error al guardar la marca No Enviar.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = useCallback((row) => {
        gdAlertify.confirm({
            title: "Eliminar Marca",
            message: "Esta marca dejara de estar activa para este auxiliar y paciente. Desea continuar?",
            okLabel: "Confirmar",
            cancelLabel: "Cancelar",
            onConfirm: async () => {
                try {
                    const response = await axiosInstance.post(`/auxiliar/no-enviar-paciente/eliminar/${row.id}`, {
                        auxiliar_id: auxiliarID,
                        paciente_id: pacienteID,
                    });

                    if (response.data?.error) {
                        gdAlertify.notiError(response.data.message || "No se pudo eliminar la marca.");
                        return;
                    }

                    gdAlertify.notiSuccess(response.data?.message || "Marca eliminada correctamente.");
                    await loadHistory();
                    onChange();
                } catch (error) {
                    console.error("Error al eliminar No Enviar:", error);
                    gdAlertify.notiError("Error al eliminar la marca.");
                }
            },
        });
    }, [auxiliarID, pacienteID, loadHistory, onChange]);

    const statusPreview = getNoEnviarPacienteStatus({
        no_enviar_historial: history,
    });

    const showPacienteColumn = history.some((row) => row.paciente_nombre || row.pacienteNombre);

    const columns = useMemo(() => {
        const baseColumns = [];

        if (showPacienteColumn) {
            baseColumns.push({
                name: "paciente_nombre",
                title: "Paciente",
                render: (row) => row.paciente_nombre || row.pacienteNombre || "-",
                width: 220,
            });
        }

        baseColumns.push(
            {
                name: "fecha_registro",
                title: "Fecha",
                render: (row) => formatDate(row.fecha_registro || row.fecha || row.date),
                width: 120,
            },
            {
                name: "observacion",
                title: "Observacion",
                render: (row) => row.observacion || row.observation || "-",
                width: 360,
            },
            {
                name: "estado",
                title: "Estado",
                render: (row) => (
                    <span className={`badge ${isNoEnviarRecordActive(row) ? "bg-warning text-dark" : "bg-secondary"}`}>
                        {isNoEnviarRecordActive(row) ? "Activo" : "Eliminado"}
                    </span>
                ),
                width: 100,
            },
            {
                name: "id",
                title: "Accion",
                render: (row) =>
                    isNoEnviarRecordActive(row) ? (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(row)}
                            title="Eliminar marca activa"
                        >
                            <i className="bi bi-trash"></i>
                        </button>
                    ) : (
                        <span className="text-muted small">-</span>
                    ),
                width: 90,
            }
        );

        return baseColumns;
    }, [showPacienteColumn, handleDelete]);

    if (!isOpen) return null;

    return (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header bg-warning text-dark">
                        <h5 className="modal-title">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            No Enviar a Este Domicilio
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>

                    <div className="modal-body">
                        <div className="alert alert-light border mb-4">
                            <div><strong>Paciente:</strong> {nombrePaciente || "-"}</div>
                            <div><strong>Auxiliar:</strong> {nombreAuxiliar || "-"}</div>
                            <div className="mt-2">
                                {statusPreview.active ? (
                                    <span className="badge bg-warning text-dark">Marca activa: NO ENVIAR A ESTE DOMICILIO</span>
                                ) : (
                                    <span className="badge bg-success">Sin marca activa</span>
                                )}
                            </div>
                            {statusPreview.active && statusPreview.observation && (
                                <div className="small mt-2 text-dark">
                                    <strong>Ultima observacion:</strong> {statusPreview.observation}
                                </div>
                            )}
                        </div>

                        <form onSubmit={handleSave} className="card border-0 bg-light p-3 mb-4">
                            <h6 className="fw-bold text-warning-emphasis mb-3">Registrar marca para esta dupla</h6>
                            {activeRecord ? (
                                <div className="alert alert-warning mb-3">
                                    Esta dupla ya tiene una marca activa. Si desea volver a marcar, primero elimine el registro activo.
                                </div>
                            ) : (
                                <div className="mb-3">
                                    <label className="form-label fw-bold small">Observacion</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        placeholder="Motivo por el cual este auxiliar no debe ser enviado a este domicilio..."
                                        value={observacion}
                                        onChange={(e) => setObservacion(e.target.value)}
                                        disabled={isSubmitting}
                                    />
                                    <small className="text-muted">
                                        Al guardar, se agrega una nota tambien al recordatorio del paciente.
                                    </small>
                                </div>
                            )}

                            <div className="text-end">
                                <button
                                    type="submit"
                                    className="btn btn-warning"
                                    disabled={isSubmitting || !!activeRecord}
                                >
                                    <i className="bi bi-ban me-2"></i>
                                    {isSubmitting ? "Guardando..." : "Marcar No Enviar"}
                                </button>
                            </div>
                        </form>

                        <h6 className="fw-bold border-bottom pb-2 mb-3">Historial</h6>
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-warning spinner-border-sm" role="status"></div>
                                <p className="small mt-2 mb-0">Cargando historial...</p>
                            </div>
                        ) : history.length > 0 ? (
                            <GdTable
                                columns={columns}
                                data={history}
                                defaultItemsPerPage={10}
                                itemsPerPageOptions={[10, 20]}
                            />
                        ) : (
                            <div className="text-center py-4 text-muted border rounded bg-white">
                                <i className="bi bi-info-circle fs-4 d-block mb-2"></i>
                                <p className="small mb-0">No hay marcas registradas para esta dupla.</p>
                            </div>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NoEnviarPacienteAuxiliarModal;
